"use client";

import { useState, useEffect, useCallback } from "react";
import { consentRequired, storedChoice, CONSENT_KEY, analyticsStorage } from "@/lib/consent-region";

/**
 * Consent banner for the site's own visit counter.
 *
 * Google Analytics was removed on October 12, 2026, and the site loads no ad
 * trackers. The only thing left to ask about is the random device ID that
 * the site's own tracker (lib/track.ts) saves in the browser.
 *
 * - Consent countries (EU, EEA, UK, Switzerland) and Washington state (see
 *   lib/consent-region.ts): banner shown, and the tracker keeps its ID in
 *   memory only until the visitor accepts. Anything saved earlier, before
 *   the rule applied or after a Decline, is removed.
 * - Everyone else: no banner.
 */

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    if (!consentRequired()) return;
    const choice = storedChoice();
    if (choice === "accepted") return;
    // Not accepted: nothing from earlier visits stays saved.
    analyticsStorage.clearSaved();
    if (choice === "declined") return;
    setShowBanner(true);
  }, []);

  const handleAccept = useCallback(() => {
    try { localStorage.setItem(CONSENT_KEY, "accepted"); } catch {}
    analyticsStorage.persist();
    setShowBanner(false);
  }, []);

  const handleDecline = useCallback(() => {
    try { localStorage.setItem(CONSENT_KEY, "declined"); } catch {}
    setShowBanner(false);
  }, []);

  if (!showBanner) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: "#1a1a2e",
        color: "#e0e0e0",
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        flexWrap: "wrap",
        fontSize: "14px",
        lineHeight: "1.5",
        boxShadow: "0 -2px 10px rgba(0,0,0,0.2)",
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <span style={{ maxWidth: "640px" }}>
        May we save a random ID in your browser to count return visits? It links the pages
        you read here, is never tied to your name or email, and is never sold or shared with
        advertisers. You can change your mind any time.{" "}
        <a href="/consumer-health-data#your-choice" style={{ color: "#7FE0C6", textDecoration: "underline" }}>Details</a>
      </span>
      <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
        <button
          onClick={handleAccept}
          style={{
            backgroundColor: "#16a394",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "8px 20px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Accept
        </button>
        <button
          onClick={handleDecline}
          style={{
            backgroundColor: "transparent",
            color: "#a0a0a0",
            border: "1px solid #444",
            borderRadius: "6px",
            padding: "8px 20px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Decline
        </button>
      </div>
    </div>
  );
}
